import { useQuery } from "@apollo/client";
import { useMsal } from "@azure/msal-react";

import { tenantById, TTenantByIdQueries } from "@store/graphql/groups/tenant/query.graphql";
import { ECommandResultStatus, TenantQueriesGetByIdArgs } from "@store/graphql/helpers/types.graphql";

export interface CurrentTenant {
  /** The tenant (company) display name, or "" when it cannot be resolved. */
  tenantName: string;
  /** License expiration date (ISO string), or null when there is no expiration. */
  licenseExpiresAt: string | null;
}

/**
 * Resolves the signed-in user's tenant (company) from the
 * `extension_TenantId` token claim via GraphQL. Apollo caches the lookup,
 * so repeated mounts do not refetch. Returns empty / null values when the
 * tenant cannot be resolved — the header and launcher must never block on
 * this.
 */
export const useCurrentTenant = (): CurrentTenant => {

  const { instance } = useMsal();

  const tenantId = instance.getActiveAccount()?.idTokenClaims?.extension_TenantId as string | undefined;

  // `licenseExpiresAt` was added to the backend TenantType in PR #3 — the
  // generated frontend types are regenerated by `npm run codegen` against
  // the deployed schema. Cast around the field name until codegen catches
  // up, so the hook keeps compiling against stale generated types.
  const fields = ["id", "name", "licenseExpiresAt"] as unknown as Parameters<typeof tenantById>[0];

  const { data } = useQuery<TTenantByIdQueries, TenantQueriesGetByIdArgs>(
    tenantById(fields),
    {
      variables: { id: tenantId ?? "" },
      skip: !tenantId
    }
  );

  const response = data?.tenant.getByID;

  const tenant = response?.status === ECommandResultStatus.SUCCESS
    ? (response.result as (typeof response.result & { licenseExpiresAt?: string | null }) | undefined)
    : undefined;

  return {
    tenantName: tenant?.name ?? "",
    licenseExpiresAt: tenant?.licenseExpiresAt ?? null
  };
};
